import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Loader2, Check, X, Trash2, Star, RefreshCw
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { useToast } from '../../hooks/use-toast';
import { useAuth } from '../../AuthContext';
import { adminApi } from '../../services/api';
import { supabase } from '../../supabaseClient';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';

const AdminReviewsPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { isAuthenticated } = useAuth();
  const [loading, setLoading] = useState(true);
  const [reviews, setReviews] = useState([]);
  const [filter, setFilter] = useState('pending');
  const [updating, setUpdating] = useState(null);

  const fetchReviews = useCallback(async () => {
    try {
      setLoading(true);
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;
      
      if (!token) {
        navigate('/login');
        return;
      }
      
      const approved = filter === 'all' ? null : filter === 'approved';
      const data = await adminApi.getReviews(token, approved);
      setReviews(data);
    } catch (err) {
      console.error('Error fetching reviews:', err);
      if (err.message.includes('403')) {
        navigate('/admin');
      }
      toast({
        title: "Error",
        description: err.message || "Failed to load reviews",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  }, [filter, navigate, toast]);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchReviews();
  }, [isAuthenticated, navigate, fetchReviews]);

  const handleApprove = async (reviewId) => {
    try {
      setUpdating(reviewId);
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;
      
      await adminApi.approveReview(reviewId, token);
      toast({ title: "Success", description: "Review approved" });
      fetchReviews();
    } catch (err) {
      toast({
        title: "Error",
        description: err.message || "Failed to approve review",
        variant: "destructive"
      });
    } finally {
      setUpdating(null);
    }
  };

  const handleReject = async (reviewId) => {
    try {
      setUpdating(reviewId);
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;
      
      await adminApi.rejectReview(reviewId, token);
      toast({ title: "Success", description: "Review rejected" });
      fetchReviews();
    } catch (err) {
      toast({
        title: "Error",
        description: err.message || "Failed to reject review",
        variant: "destructive"
      });
    } finally {
      setUpdating(null);
    }
  };

  const handleDelete = async (reviewId) => {
    if (!window.confirm('Are you sure you want to delete this review?')) return;
    
    try {
      setUpdating(reviewId);
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;
      
      await adminApi.deleteReview(reviewId, token);
      toast({ title: "Success", description: "Review deleted" });
      fetchReviews();
    } catch (err) {
      toast({
        title: "Error",
        description: err.message || "Failed to delete review",
        variant: "destructive"
      });
    } finally {
      setUpdating(null);
    }
  };

  if (!isAuthenticated) return null;

  return (
    <div style={{ backgroundColor: 'var(--bg-primary)', minHeight: '100vh', paddingTop: '80px' }} data-testid="admin-reviews">
      <div className="container py-8">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Link to="/admin">
            <Button variant="outline" size="icon" style={{ borderRadius: 0 }}>
              <ArrowLeft size={20} />
            </Button>
          </Link>
          <div className="flex-1">
            <h1 className="hero-medium">Manage Reviews</h1>
          </div>
          <Button variant="outline" onClick={fetchReviews} style={{ borderRadius: 0 }}>
            <RefreshCw size={16} className="mr-2" />
            Refresh
          </Button>
        </div>

        {/* Filters */}
        <div className="flex gap-4 mb-8">
          <div>
            <label className="body-small block mb-2" style={{ color: 'var(--text-secondary)' }}>
              Filter
            </label>
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger className="w-[180px]" style={{ borderRadius: 0 }} data-testid="review-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="pending">Pending Approval</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="all">All Reviews</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Reviews List */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="animate-spin" size={32} style={{ color: 'var(--olive-primary)' }} />
          </div>
        ) : reviews.length === 0 ? (
          <div className="p-12 text-center" style={{ backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--border-light)' }}>
            <p className="body-large" style={{ color: 'var(--text-secondary)' }}>
              No reviews found
            </p>
          </div>
        ) : (
          <div className="space-y-4" data-testid="reviews-list">
            {reviews.map((review) => (
              <div
                key={review.id}
                className="p-6"
                style={{ backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--border-light)' }}
                data-testid={`review-${review.id}`}
              >
                <div className="flex flex-col lg:flex-row lg:items-start gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      <h3 className="heading-3">{review.guest_name}</h3>
                      <div className="flex gap-1">
                        {[...Array(5)].map((_, i) => (
                          <Star
                            key={i}
                            size={16}
                            fill={i < review.rating ? 'var(--text-primary)' : 'none'}
                            style={{ color: 'var(--text-primary)' }}
                          />
                        ))}
                      </div>
                      <span
                        className="px-2 py-1 text-xs uppercase"
                        style={{ 
                          backgroundColor: review.approved ? '#22c55e' : '#eab308', 
                          color: 'white' 
                        }}
                      >
                        {review.approved ? 'Approved' : 'Pending'}
                      </span>
                    </div>
                    
                    <p className="body-regular mb-3" style={{ color: 'var(--text-primary)', lineHeight: 1.6 }}>
                      "{review.review_text}"
                    </p>
                    
                    <p className="body-small" style={{ color: 'var(--text-light)' }}>
                      {review.country} • {new Date(review.created_at).toLocaleDateString()}
                    </p>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2">
                    {!review.approved && (
                      <Button
                        size="sm"
                        className="btn-primary flex items-center gap-1"
                        onClick={() => handleApprove(review.id)}
                        disabled={updating === review.id}
                        data-testid={`approve-${review.id}`}
                      >
                        {updating === review.id ? (
                          <Loader2 size={14} className="animate-spin" />
                        ) : (
                          <Check size={14} />
                        )}
                        Approve
                      </Button>
                    )}
                    {review.approved && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="flex items-center gap-1"
                        onClick={() => handleReject(review.id)}
                        disabled={updating === review.id}
                        style={{ borderRadius: 0 }}
                        data-testid={`reject-${review.id}`}
                      >
                        <X size={14} />
                        Unapprove
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="outline"
                      className="flex items-center gap-1"
                      onClick={() => handleDelete(review.id)}
                      disabled={updating === review.id}
                      style={{ borderRadius: 0, borderColor: '#ef4444', color: '#ef4444' }}
                      data-testid={`delete-${review.id}`}
                    >
                      <Trash2 size={14} />
                      Delete
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminReviewsPage;
